import { DEFAULT_Z_INDEX } from '@/domain/defaultV2Configuration';

export const HOST_CONTAINER_ID = 'gladlyChat_container';

export const WIDGET_ROOT_ID = 'gladly-chat-react-root';

const HOST_LANDMARK_LABEL = 'Live chat';

/**
 * Whether the React widget has mounted into the host's shadow root.
 *
 * Read from the DOM because bootstrap's own loaded flag is set only once
 * startFullWidget resolves, which cannot tell a failure before the mount from
 * one after it.
 */
export function isWidgetMounted(): boolean {
  return Boolean(document.getElementById(HOST_CONTAINER_ID)?.shadowRoot?.querySelector(`#${WIDGET_ROOT_ID}`));
}

/**
 * Apply the configured stacking to the host element (#gladlyChat_container).
 *
 * The host is a fixed-position, z-indexed element, which makes it its own
 * stacking context. Every V2 surface (launcher, bootstrap input, and the React
 * panel inside the shadow root) lives inside it, so a z-index set on those
 * descendants only orders them relative to each other — it can't change how the
 * widget as a whole stacks against the customer's page. The host is therefore
 * the single element whose z-index governs on-page stacking.
 *
 * When the V1 bundle is also present, V1's global.css pins the host at
 * z-index 2147483000, so a config-driven z-index applied anywhere else appears
 * to be a no-op. V1's build runs its CSS through cssimportant-loader
 * (web/config/cssConfig.shared.js), so every declaration it ships — including
 * that pin — carries !important, which beats a plain inline style. Writing
 * position + z-index inline WITH !important is the only cascade origin that
 * outranks it, so the configured value actually governs the widget's stacking.
 *
 * `display` is pinned for the same cascade reason but a different failure: a
 * page rule or a browser extension matching the host — ad and cookie blockers
 * routinely hit ids like this one — can set `display: none` and the widget
 * vanishes with nothing in our own code to explain it. Inline `!important` is
 * the highest author-origin priority, so it outranks any stylesheet the page or
 * an extension injects, and it does so for every customer rather than only
 * wherever the offending rule was noticed. `block` is the host `div`'s own
 * default, so this pins the existing behaviour rather than changing layout.
 *
 * Nothing hides itself this way — bootstrap removes the host outright when
 * display rules suppress the entry point, and the widget tier leaves it mounted
 * but empty, drawing nothing — so pinning `display` cannot fight our own rules.
 */
export function applyHostZIndex(host: HTMLElement, zIndex: number = DEFAULT_Z_INDEX): void {
  host.style.setProperty('position', 'fixed', 'important');
  host.style.setProperty('z-index', String(zIndex), 'important');
  host.style.setProperty('display', 'block', 'important');
}

/**
 * Add or remove the host's `region` landmark.
 *
 * The host stays in the DOM while the widget tier renders nothing — React's
 * root lives in its shadow root, so removing the node would tear out the mount
 * point — and a labelled region with nothing inside it makes assistive tech
 * announce "Live chat" on a page that offers no chat.
 */
export function setHostLandmark(present: boolean): void {
  const host = document.getElementById(HOST_CONTAINER_ID);
  if (!host) return;
  if (present) {
    host.setAttribute('role', 'region');
    host.setAttribute('aria-label', HOST_LANDMARK_LABEL);
    return;
  }
  host.removeAttribute('role');
  host.removeAttribute('aria-label');
}
